import React, { useState } from 'react';
import { Lightbox } from './Lightbox';
import { ExternalLink } from 'lucide-react';

/**
 * Component to display images or videos intelligently
 * Tries to detect the media type and render accordingly
 * Opens Lightbox on click
 */
export const MediaDisplay = ({ 
  url, 
  alt, 
  className = '', 
  description = '',
  external_link = '',
  external_link_text = '',
  allMedia = [],
  currentIndex = 0
}) => {
  const [mediaType, setMediaType] = useState('image'); // 'image' or 'video'
  const [error, setError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Check if URL suggests it's a video
  const isLikelyVideo = url && (
    url.endsWith('.mp4') || 
    url.endsWith('.gifv') || 
    url.endsWith('.webm') ||
    url.includes('imgur.com') && !url.match(/\.(jpg|jpeg|png|gif)$/i)
  );

  const handleImageError = (e) => {
    // Ocultar imagen inmediatamente para evitar flasheo del error de Imgur
    if (e.target) {
      e.target.style.display = 'none';
    }
    
    // If image fails and URL might be video, try video
    if (isLikelyVideo) {
      setMediaType('video');
    } else {
      setError(true);
    }
  };

  const handleVideoError = (e) => {
    // Ocultar video inmediatamente
    if (e.target) {
      e.target.style.display = 'none';
    }
    
    // If video fails, try as image
    if (mediaType === 'video') {
      setMediaType('image');
    } else {
      setError(true);
    }
  };

  const handleClick = () => {
    setLightboxOpen(true);
    setLightboxIndex(currentIndex);
  };

  if (error) {
    return (
      <div className={`bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center p-8 rounded-lg ${className}`}>
        <div className="text-center">
          <p className="text-slate-400 text-sm">📷 Imagen no disponible</p>
          <p className="text-xs text-slate-300 mt-1">Actualiza la URL para ver el contenido</p>
        </div>
      </div>
    );
  }

  // Start with video if it's likely a video
  if (isLikelyVideo && mediaType === 'video') {
    return (
      <>
        <div className="space-y-2">
          <video
            src={url}
            className={`${className} cursor-pointer hover:opacity-90 transition-opacity`}
            controls
            loop
            muted
            playsInline
            onError={handleVideoError}
            onClick={handleClick}
          >
            Tu navegador no soporta el elemento de video.
          </video>
          {description && (
            <p className="text-sm text-slate-600">{description}</p>
          )}
          {external_link && external_link_text && (
            <a
              href={external_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              {external_link_text}
            </a>
          )}
        </div>
        {lightboxOpen && (
          <Lightbox
            mediaUrl={allMedia.length > 0 ? allMedia[lightboxIndex].url : url}
            mediaType="video"
            onClose={() => setLightboxOpen(false)}
            allMedia={allMedia}
            currentIndex={lightboxIndex}
            onNavigate={setLightboxIndex}
          />
        )}
      </>
    );
  }

  // Default to image
  return (
    <>
      <div className="space-y-2">
        <img
          src={url}
          alt={alt || description || 'Imagen'}
          className={`${className} cursor-pointer hover:opacity-90 transition-opacity`}
          style={{ minHeight: error ? '0' : 'auto' }}
          onError={handleImageError}
          onClick={handleClick}
          loading="lazy"
        />
        {description && (
          <p className="text-sm text-slate-600">{description}</p>
        )}
        {external_link && external_link_text && (
          <a
            href={external_link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {external_link_text}
          </a>
        )}
      </div>
      {lightboxOpen && (
        <Lightbox
          mediaUrl={allMedia.length > 0 ? allMedia[lightboxIndex].url : url}
          mediaType="image"
          onClose={() => setLightboxOpen(false)}
          allMedia={allMedia}
          currentIndex={lightboxIndex}
          onNavigate={setLightboxIndex}
        />
      )}
    </>
  );
};
